import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {
    APPOINTMENT_BOOK_STATE_INIT,
    appointmentBooksState,
    appointmentsModalHelperState,
    createAppointmentSlot,
    isAssignSessionModalOpen,
    schedulerFacility,
    selectedDate,
    selectedPatient,
    setAppointmentBooksState,
    setModalHelperState,
    swimLanesViewOpen,
} from '../../../../../store/reducers/appointments';
import {useCallback, useEffect, useState} from 'react';
import moment from 'moment';
import {setAgyleHeaderLoading, setSnackbar} from '../../../../../store/reducers/globals';
import Grid from '@mui/material/Grid';
import WardPanelSearch from '../../../../ward/map/components/panels/wardPanelSearch';
import {cloneDeep, isEmpty, isFunction} from 'lodash';
import AgyleDialog from '../../../../../components/modal/agyleDialog';
import PropTypes from 'prop-types';
import {useRefresh} from '../../../context/refreshContext';
import {APPOINTMENT_MODALS} from '../../../utils/constants/appointmentSessionConstants';
import SelectedPatientContext from './selectedPatientContext';
import {useSessionStyles} from './useSessionStyles';
import AppointmentLinkedAttendance from './appointmentLinkedAttendance';
import {APPOINTMENT_BOOK_TYPES} from '../../../../ward/map/patientList/utils/edReviewUtils';
import useSchedulerPermissions from '../../calendarView/dailyCalendar/hooks/useSchedulerPermissions';

const AssignSessionModal = ({ dischargeContext, onClose, patientObj, onCloseModal, setSchedulerHour}) => {
  const classes = useSessionStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { onCalendarRefresh, onSlotRefresh } = useRefresh();

  // const isBarsCtxActive = useSelector(barsContextActive);
  const isModalOpen = useSelector(isAssignSessionModalOpen);
  const modalHelperState = useSelector(appointmentsModalHelperState); // here it should has session itself and hour selected -> { session, hour }
  const currentlySelectedDate = useSelector(selectedDate);
  const selectedPtSlice = useSelector(selectedPatient);
  const isSwimLanesViewOpen = useSelector(swimLanesViewOpen);
  const facility = useSelector(schedulerFacility);
  const bookState = useSelector(appointmentBooksState);

  // Main form state
  const [selectedPt, setSelectedPt] = useState(null);

  const { canBookSlot: canAddPatient } = useSchedulerPermissions();

  const selectPt = (pt) => {
    const objToAssign = pt || null;

    const modalStateClone = cloneDeep(modalHelperState);

    dispatch(setAppointmentBooksState(APPOINTMENT_BOOK_STATE_INIT));
    setSelectedPt(objToAssign);
    dispatch(
      setModalHelperState({
        ...modalStateClone,
        selectedPatient: objToAssign,
      }),
    );
  };

  useEffect(() => {
    if (!!modalHelperState?.selectedPatient && !selectedPt) {
      setSelectedPt(modalHelperState?.selectedPatient);
    }
  }, [modalHelperState?.selectedPatient, selectedPt]);

  useEffect(() => {
    if (!!patientObj && !selectedPt) {
      setSelectedPt(patientObj?.patient);
    }
  }, [patientObj, selectedPt]);

  useEffect(() => {
    if (dischargeContext && selectedPtSlice) {
      setSelectedPt(selectedPtSlice);
    }
  }, [dischargeContext, selectedPtSlice]);

  const getModalTitle = (changedCtx) => {
    if (changedCtx) {
      return "Confirm Review Reservation";
    } else {
      return "Choose Patient";
    }
  };

  const closeModal = (snackbarObj = null) => {
    onClose(APPOINTMENT_MODALS.ASSIGN_SESSION);
    dispatch(setAppointmentBooksState(APPOINTMENT_BOOK_STATE_INIT));

    if (snackbarObj) {
      return dispatch(setSnackbar(snackbarObj));
    }
  };

  const handleSubmitChange = useCallback(async () => {
    if (!selectedPt || !modalHelperState?.hour || !bookState?.reason) {
      return dispatch(
        setSnackbar({
          type: "error",
          message: "Please provide valid patient, appointment hour and reason.",
        }),
      );
    }

    if (isEmpty(selectedPt) || !bookState?.reason) {
      return dispatch(
        setSnackbar({
          message: "Could not save review status. Please try again later.",
          type: "error",
        }),
      );
    }

    if ((bookState?.type !== APPOINTMENT_BOOK_TYPES.NONE && bookState?.type !== APPOINTMENT_BOOK_TYPES.UTC) && !bookState?.linkedAttendance) {
      return dispatch(
        setSnackbar({
          message: "Please select linked attendance for this appointment.",
          type: "error",
        }),
      );
    }

    const clonePatient = cloneDeep(selectedPt);
    const patientId = clonePatient?.patient?.__id ? clonePatient?.patient?.__id : clonePatient?.__id;

    const assignedDate = moment(
      `${currentlySelectedDate} ${modalHelperState?.hour}`,
      "DD/MM/YYYY h:mm a",
    ).toDate();
    const slotEndDate = moment(modalHelperState?.hour)
      .add(modalHelperState?.session.slotDurationInMinutes, "minute")
      .toDate();

    // TODO
    if (dischargeContext) {
      const passToDischargeFormObj = {
        sessionId: modalHelperState?.session?.sessionId,
        reviewReason: bookState?.reason,
        reviewDate: assignedDate,
      };

      onClose(APPOINTMENT_MODALS.ASSIGN_SESSION, passToDischargeFormObj);
      dispatch(setAppointmentBooksState(APPOINTMENT_BOOK_STATE_INIT));
      setSelectedPt(null);
    } else {
      if (bookState.linkToActivity && !bookState?.linkedAttendance?.__id) {
          dispatch(setSnackbar({
              type: 'error',
              message: 'Please select valid patient\'s activity to link.'
          }))

          return;
      }

      const slotData = {
        assignedSession: modalHelperState?.session?.sessionId,
        wardId: modalHelperState?.session?.wardId || "ED",
        hospitalId: facility || modalHelperState?.session?.hospitalId,
        startDate: moment(modalHelperState?.hour).toDate(),
        endDate: slotEndDate,
        description: bookState?.reason,
        patientId,
        outsideSessionSlotId: modalHelperState?.outsideSessionSlotId,
        linkedAttendanceId: bookState?.linkedAttendance?.__id || null,
        appointmentTye: bookState?.type || null,
      };
      dispatch(setAgyleHeaderLoading(true));
      try {
        const apiResponse = await dispatch(
          createAppointmentSlot(slotData,modalHelperState.remove, async (created) => {
            closeModal({
              message: `Patient has been successfully appointed on ${moment(created.startDate).format(window.envConfig.defaultDateTimeFormat)}`,
              type: "success",
            });
            // dispatch(addPatientSchedulerNote({
            //   ptId: patientId,
            //   newValues: slotData,
            //   patientsInWardsRef: clonePatient?.__type === 'com.fortrus.unity.api.model.PatientInWard' && clonePatient?.__id ? clonePatient?.__id : null,
            // }))

            if (onCloseModal) {
              if (setSchedulerHour) setSchedulerHour(modalHelperState?.hour);
              onCloseModal();
            }
          },
              false,
              clonePatient?.__type === 'com.fortrus.unity.api.model.PatientInWard' && clonePatient?.__id ? clonePatient?.__id : null
          ),
        );

        if (apiResponse) {
          if (isSwimLanesViewOpen && isFunction(onSlotRefresh)) {
            onSlotRefresh();
          } else if (isFunction(onCalendarRefresh)) {
            onCalendarRefresh();
          }
        }
      } catch (err) {
      } finally {
        dispatch(setAgyleHeaderLoading(false));
      }
    }
  }, [
    selectedPt,
    bookState,
    modalHelperState,
    onCalendarRefresh,
    onSlotRefresh,
    isSwimLanesViewOpen,
    currentlySelectedDate,
    modalHelperState?.hour,
  ]);

  const handleCloseModal = useCallback((_event, reason) => {
    if (reason !== "backdropClick") {
      closeModal();
    }
  }, []);

  return (
    isModalOpen && (
      <AgyleDialog
        fullWidth
        noPadding
        width="md"
        dividers={false}
        open={isModalOpen}
        closeButtonOnHeader
        disableEscapeKeyDown
        darkContentBackground
        disableActionsCloseButton
        onClose={handleCloseModal}
        onSave={handleSubmitChange}
        actionStyle={classes.actionStyle}
        submitText={t("Book Appointment")}
        contentStyle={classes.contentStyle}
        title={t(getModalTitle(dischargeContext))}
        disableSubmit={!selectedPt || !bookState?.reason || !bookState?.type || !canAddPatient}
      >
        <Grid item xs={12} className={classes.patientListContainer}>
          {!selectedPt ? (
            <Grid item xs={12} sx={{ my: 1, ml: -1, mr: 1 }}>
              <WardPanelSearch
                autoWidth
                onlyGlobal
                appointments
                maxResults={5}
              />
            </Grid>
          ) : (
            <>
              <SelectedPatientContext
                onRefuse={selectPt}
                patient={selectedPt}
                selectedHr={modalHelperState?.hour}
              />

              <Grid container item xs={12}>
                  <AppointmentLinkedAttendance patient={selectedPt} />
              </Grid>
            </>
          )}
        </Grid>
      </AgyleDialog>
    )
  );
};

AssignSessionModal.defaultProps = {
  open: false,
};

AssignSessionModal.defaultProps = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};

export default AssignSessionModal;
